import { PersonService } from "./person.service"
import type { CompanyPeople } from "./person.models"
import { colleagueCard, emptyState, escapeHtml, langFromPath, renderLocked, type Lang } from "./person-profile.component"

/**
 * <tbw-company-people> — the /company-peoples/<slug> org chart, loaded from
 * GET /companies/:slug/people. The Webflow template used to bind every person's
 * name and role in public HTML (audit 15/10); members now get them from the API,
 * Free gets the members wall, anonymous gets nothing. Same cards as before.
 */

const COPY: Record<Lang, { empty: string; notFound: string; error: string; loading: string }> = {
  en: { empty: "No people found.", notFound: "This company does not exist.", error: "Unable to load this list.", loading: "Loading..." },
  fr: { empty: "Aucune personne trouvée.", notFound: "Cette entreprise n'existe pas.", error: "Impossible de charger cette liste.", loading: "Chargement..." },
  de: { empty: "Keine Personen gefunden.", notFound: "Dieses Unternehmen existiert nicht.", error: "Liste konnte nicht geladen werden.", loading: "Wird geladen..." },
  nl: { empty: "Geen personen gevonden.", notFound: "Dit bedrijf bestaat niet.", error: "Kan deze lijst niet laden.", loading: "Laden..." },
}

/** /company-peoples/<slug>, /fr/company-peoples/<slug>, … → <slug>; null otherwise. */
export function companySlugFromPath(pathname: string): string | null {
  const match = pathname.match(/^(?:\/(?:fr|de|nl))?\/company-peoples\/([^/?#]+)\/?$/)
  return match ? decodeURIComponent(match[1]) : null
}

export function renderCompanyPeople(data: CompanyPeople, lang: Lang): string {
  if (!data.people.length) return emptyState(COPY[lang].empty)
  const cover = data.company.logoUrl ?? null
  return `<div class="ocean-results-wrapper"><div class="results-list-company-people" role="list">${
    data.people.map((p) => colleagueCard(p, cover, lang)).join("")
  }</div></div>`
}

export class CompanyPeopleComponent extends HTMLElement {
  // Not `lang`: HTMLElement already has one.
  private readonly pageLang: Lang = langFromPath(window.location.pathname)

  constructor(private personService: PersonService = window.tbwServices.personService) {
    super()
  }

  async connectedCallback() {
    const slug = companySlugFromPath(window.location.pathname)
    if (!slug) return
    await this.waitForAuth()
    if (!window.tbwServices.authService.isAuthenticated) return
    const copy = COPY[this.pageLang]
    this.innerHTML = `<p class="body3">${escapeHtml(copy.loading)}</p>`
    const result = await this.personService.companyPeople(slug, this.pageLang)
    if (result.status === "ok") this.innerHTML = renderCompanyPeople(result.data, this.pageLang)
    else if (result.status === "locked") this.innerHTML = renderLocked(this.pageLang)
    else this.innerHTML = `<p class="body3">${escapeHtml(result.status === "not_found" ? copy.notFound : copy.error)}</p>`
  }

  private async waitForAuth(timeoutMs = 15000): Promise<void> {
    const start = Date.now()
    while (!window.tbwServices.authService.isAuthenticated) {
      if (Date.now() - start > timeoutMs) return
      await new Promise((r) => setTimeout(r, 200))
    }
  }
}
